import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { brand, ease, duration, fonts, streamPalettes } from '../design/palettes';
import { PoolDishMark, Wordmark } from '../design/primitives';
import { registrationEnabled } from '../config/registration';

// Public marketing landing page. Mirrors `Marketing site.html` from the design
// handoff: hero with mark + app card, three features, colossal serif footer
// wordmark, full footer. The nav + hero carry the "Get started" → /register CTA
// now that public registration is open; "Log in" → /login lives in the nav only.
// Authenticated users are redirected straight to /app — they don't need the
// front door.

const c = streamPalettes.Lagoon;

export function MarketingPage() {
  const { isAuthenticated, isLoading } = useAuth();
  if (!isLoading && isAuthenticated) return <Navigate to="/app" replace />;

  return (
    // The marketing landing page is light-only — pin the subtree to the light
    // theme so it renders identically regardless of the app/OS theme.
    <div className="mk-page" data-theme="light" style={pageStyle}>
      <MarketingStyles />
      <MarketingNav />
      <main>
        <Hero />
        <Features />
        <GooeyPlus />
      </main>
      <Footer />
    </div>
  );
}

const pageStyle: React.CSSProperties = {
  background: brand.paper,
  color: brand.ink,
  fontFamily: fonts.sans,
  WebkitFontSmoothing: 'antialiased',
  minHeight: '100vh',
  lineHeight: 1.5,
};

// One-off page-specific keyframes for the hero mark drop-in.
// The shared `goo` filter is mounted globally from <App />.
function MarketingStyles() {
  return (
    <style>{`
      @keyframes mk-drop {
        0%   { transform: translateY(-60px) scale(.6); opacity: 0; }
        40%  { transform: translateY(0)     scale(1);  opacity: 1; }
        55%  { transform: translateY(0)     scale(1.15); }
        100% { transform: translateY(0)     scale(1); }
      }
      @keyframes mk-dish {
        0%,40%,100% { transform: scaleX(1)    scaleY(1); }
        55%         { transform: scaleX(1.06) scaleY(.92); }
        70%         { transform: scaleX(.98)  scaleY(1.04); }
        85%         { transform: scaleX(1)    scaleY(1); }
      }
      .mk-mark-anim .mk-dropbead { animation: mk-drop ${duration.idle} ${ease.gel} infinite; transform-origin: 50px 38px; transform-box: fill-box; }
      .mk-mark-anim .mk-dish     { animation: mk-dish ${duration.idle} ${ease.gel} infinite; transform-origin: 50px 62px; transform-box: fill-box; }
      @media (prefers-reduced-motion: reduce) {
        .mk-mark-anim .mk-dropbead, .mk-mark-anim .mk-dish { animation: none; }
      }
      ::selection { background: ${brand.lagoon}; color: ${brand.paper}; }

      /* Grid + flex items default to min-width:auto and refuse to shrink below
         their content's intrinsic (min-content) width. The hero's app-card
         preview carries wide nowrap content (the mono balance figure, the header
         buttons, the stats row), so without this its grid column is forced wider
         than the viewport — the card spills past the right edge and gets clipped
         (and forces horizontal scroll on phones). min-width:0 lets the column
         collapse so the card's width:100%/max-width actually wins. Applies at all
         widths, so it also fixes the 860–1100px range before the stack kicks in;
         harmless on wide desktop where the content already fits. The app-card's
         own width:100%+max-width keeps it sharp and capped at 460px on desktop. */
      .mk-hero > *,
      .mk-hero-visual { min-width: 0; }

      /* Tablet + mobile: collapse the two-column hero, stack feature cards.
         JSX uses inline style={}, which beats class selectors on specificity, so anything that overrides
         an inline-set property here needs !important. */
      @media (max-width: 860px) {
        .mk-page       { overflow-x: hidden; }
        .mk-nav-inner  { padding: 14px 20px !important; gap: 12px !important; }
        .mk-hero       { grid-template-columns: 1fr !important; padding: 56px 20px 32px !important; gap: 40px !important; }
        .mk-hero-h1    { font-size: clamp(44px, 10vw, 72px) !important; letter-spacing: -1.6px !important; }
        .mk-hero-visual{ min-height: 0 !important; }
        .mk-bg-mark    { display: none !important; }
        .mk-mark-anim  { width: 110px !important; height: 110px !important; top: -8px !important; right: 8px !important; }
        .mk-app-card   { transform: none !important; max-width: 100% !important; }
        .mk-features   { grid-template-columns: 1fr !important; margin-top: 64px !important; padding: 0 20px !important; gap: 16px !important; }
        .mk-feature-card { min-height: 0 !important; padding: 26px 22px !important; }
        /* Side margin (not just padding) so the rounded dark band sits inset
           from the viewport edges on phones instead of spanning full width. */
        .mk-gplus      { margin: 80px 16px 0 !important; padding: 56px 24px !important; }
        .mk-gplus-grid { grid-template-columns: 1fr !important; gap: 14px !important; }
        .mk-plan-compare { grid-template-columns: 1fr !important; gap: 14px !important; }
        .mk-footer     { margin-top: 80px !important; padding: 36px 20px !important; }
      }
      @media (max-width: 600px) {
        .mk-nav-plus { display: none !important; }
      }
      @media (max-width: 480px) {
        .mk-hero-cta-row { flex-direction: column !important; align-items: flex-start !important; gap: 12px !important; }
        /* On narrow phones the app-card header (logo, wordmark, view toggle,
           bell, "+ New", "Log out") is wider than the card, so the card's
           overflow:hidden clips the trailing "Log out" button. Drop the two
           least-essential chrome items so the action buttons stay fully on
           screen. */
        .mk-card-toggle, .mk-card-bell { display: none !important; }
      }
    `}</style>
  );
}

function MarketingNav() {
  return (
    <nav style={navStyle}>
      <div className="mk-nav-inner" style={navInnerStyle}>
        <div style={brandLinkStyle}>
          <PoolDishMark size={30} color={brand.lagoon} />
          <Wordmark c={c} size={24} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <a href="#gooey-plus" className="mk-nav-plus" style={navPlusStyle}>
            Gooey<span style={{ color: brand.lagoon }}>+</span>
          </a>
          <Link to="/blog" className="mk-nav-plus" style={navLoginStyle}>Blog</Link>
          <Link to="/login" style={navLoginStyle}>Log in</Link>
          {/* Sign-up kill switch: the CTA would land on a page that can only
              turn people away, so it collapses into the Log in link. */}
          {registrationEnabled && (
            <Link to="/register" style={ctaButtonStyle}>Get started</Link>
          )}
        </div>
      </div>
    </nav>
  );
}

const navLoginStyle: React.CSSProperties = {
  color: brand.ink, textDecoration: 'none', fontWeight: 600, fontSize: 14,
};
const navPlusStyle: React.CSSProperties = {
  color: brand.ink, textDecoration: 'none', fontWeight: 600, fontSize: 14,
  fontFamily: fonts.display, letterSpacing: -0.2,
};
const ctaButtonStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 8,
  background: brand.ink, color: '#F4F2EC', textDecoration: 'none',
  fontSize: 14, fontWeight: 600, borderRadius: 999, padding: '10px 18px',
};

const navStyle: React.CSSProperties = {
  position: 'sticky', top: 0, zIndex: 10,
  background: 'rgba(244,242,236,.85)', backdropFilter: 'blur(14px)',
  WebkitBackdropFilter: 'blur(14px)',
  borderBottom: `1px solid ${brand.lagoonAqua}33`,
};
const navInnerStyle: React.CSSProperties = {
  maxWidth: 1280, margin: '0 auto', padding: '16px 32px',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24,
};
const brandLinkStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, color: brand.ink, textDecoration: 'none',
};

function Hero() {
  return (
    <section className="mk-hero" style={heroStyle}>
      <div>
        <h1 className="mk-hero-h1" style={heroH1Style}>
          Money, <em style={{ color: brand.lagoon, fontStyle: 'italic' }}>on schedule.</em>
        </h1>
        <p style={ledeStyle}>
          Gooey shows your <strong style={{ color: brand.ink, fontWeight: 500 }}>real balance</strong> — today, next
          Friday, the 1st. Every paycheck and every bill, drawn as a curve you can read in a glance.
        </p>
        <div className="mk-hero-cta-row" style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 36, flexWrap: 'wrap' }}>
          {registrationEnabled ? (
            <Link to="/register" style={{ ...ctaButtonStyle, padding: '14px 24px', fontSize: 15 }}>Get started</Link>
          ) : (
            <Link to="/login" style={{ ...ctaButtonStyle, padding: '14px 24px', fontSize: 15 }}>Log in</Link>
          )}
        </div>
      </div>
      <HeroVisual />
    </section>
  );
}

const heroStyle: React.CSSProperties = {
  maxWidth: 1280, margin: '0 auto', padding: '96px 32px 64px',
  display: 'grid', gridTemplateColumns: '1.05fr 1fr', gap: 64, alignItems: 'center', position: 'relative',
};
const heroH1Style: React.CSSProperties = {
  fontFamily: fonts.brand, fontWeight: 400, fontSize: 'clamp(56px, 8vw, 96px)',
  lineHeight: 0.95, letterSpacing: -2.8, margin: 0, color: brand.ink, maxWidth: 680,
};
const ledeStyle: React.CSSProperties = {
  fontSize: 19, color: '#5d6671', maxWidth: 480, margin: '28px 0 0', lineHeight: 1.5,
};

function HeroVisual() {
  return (
    <div className="mk-hero-visual" style={heroVisStyle}>
      <div className="mk-bg-mark" style={bgMarkStyle}>
        <PoolDishMark size={520} color={brand.lagoon} />
      </div>
      <svg className="mk-mark-anim" style={markAnimStyle} viewBox="0 0 100 100" aria-hidden>
        <g filter="url(#goo)" fill={brand.lagoon}>
          <ellipse className="mk-dish" cx="50" cy="62" rx="32" ry="18" />
          <circle cx="34" cy="44" r="8" />
          <circle cx="68" cy="46" r="7" />
          <circle className="mk-dropbead" cx="50" cy="38" r="10" />
        </g>
      </svg>
      <AppCard />
    </div>
  );
}

const heroVisStyle: React.CSSProperties = {
  position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 520,
};
const bgMarkStyle: React.CSSProperties = {
  position: 'absolute', top: -40, right: -80, width: 520, height: 520,
  opacity: 0.1, pointerEvents: 'none',
};
const markAnimStyle: React.CSSProperties = {
  position: 'absolute', top: 8, right: 24, width: 170, height: 170,
};

// The hero "app card" is a faithful static replica of the real scheduler
// screen (header → balance + projected stats → search → day groups with an
// end-of-day running balance), so the marketing site previews what users
// actually get. Sample data only — not wired to anything.
const mkMute = '#5d6671';
const mkLine = '#e6e2d8';
const mkGreen = '#127a55';

function AppCard() {
  return (
    <div className="mk-app-card" style={appCardStyle}>
      <AppCardHeader />
      <AppCardBalance />
      <div style={{ padding: '4px 16px 14px' }}>
        <AppCardDay
          dow="Fri" day="5" mon="Jun" eod="$1,846.50"
          txns={[
            { name: 'Paycheck', amt: '+ $635.34', kind: 'in' },
            { name: 'Car Payment', amt: '− $369.38', kind: 'out' },
            { name: 'Household spending', amt: '− $250.00', kind: 'out' },
            { name: 'Streaming Service', amt: '− $11.96', kind: 'out' },
          ]}
        />
        <AppCardDay
          dow="Sun" day="7" mon="Jun" eod="$1,612.19" last
          txns={[
            { name: 'Internet Service', amt: '− $110.60', kind: 'out' },
            { name: 'Water + Trash', amt: '− $123.71', kind: 'out' },
          ]}
        />
      </div>
    </div>
  );
}

function AppCardHeader() {
  return (
    <div style={cardHeadStyle}>
      <PoolDishMark size={20} color={brand.lagoon} />
      <Wordmark c={c} size={17} />
      <div className="mk-card-toggle" style={cardToggleStyle}>
        <span style={{ ...cardSegStyle, ...cardSegActiveStyle }}>
          <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden>
            <path d="M2 3h8M2 6h8M2 9h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </span>
        <span style={cardSegStyle}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
            <rect x="1.5" y="2.5" width="9" height="8" rx="1.4" stroke="currentColor" strokeWidth="1.2" />
            <path d="M1.5 5h9M4 1.2v2M8 1.2v2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
        </span>
      </div>
      <div style={{ flex: 1 }} />
      <span className="mk-card-bell" style={cardBellStyle}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M6 8a6 6 0 0112 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 003.4 0" />
        </svg>
      </span>
      <span style={cardNewBtnStyle}>＋ New</span>
      <span style={cardLogoutBtnStyle}>Log out</span>
    </div>
  );
}

function AppCardBalance() {
  return (
    <div style={cardBalStyle}>
      <div style={cardBalRowStyle}>
        <div style={cardOverlineStyle}>Current balance</div>
        <div style={cardBalNumStyle}>
          $1,842.50
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={mkMute} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M12 20h9" />
            <path d="M16.5 3.5a2.12 2.12 0 013 3L7 19l-4 1 1-4z" />
          </svg>
        </div>
      </div>
      <div style={cardStatsRowStyle}>
        <CardStat align="left" label="In 30 days" value="$601.15" />
        <CardStat align="center" label="Lowest point" value="$601.15" sub="Jun 25" />
        <CardStat align="right" label="Next paycheck" value="$635.34" sub="Jun 5" />
      </div>
      <div style={cardSearchStyle}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={mkMute} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </svg>
        Search transactions
      </div>
    </div>
  );
}

function CardStat({ label, value, sub, align }: { label: string; value: string; sub?: string; align: 'left' | 'center' | 'right' }) {
  return (
    <div style={{ flex: 1, textAlign: align }}>
      <div style={cardStatLblStyle}>{label}</div>
      <div style={cardStatValStyle}>{value}</div>
      {sub && <div style={cardStatSubStyle}>{sub}</div>}
    </div>
  );
}

type CardTxn = { name: string; amt: string; kind: 'in' | 'out' };

function AppCardDay({ dow, day, mon, eod, txns, last = false }: { dow: string; day: string; mon: string; eod: string; txns: CardTxn[]; last?: boolean }) {
  return (
    <div style={{ padding: '12px 0', borderBottom: last ? 'none' : `1px solid ${mkLine}` }}>
      <div style={cardDayHeadStyle}>
        <span style={cardDowStyle}>{dow}</span>
        <span style={cardDayNumStyle}>{day}</span>
        <span style={cardDayMonStyle}>{mon}</span>
      </div>
      {txns.map((t) => (
        <div key={t.name} style={cardTxnStyle}>
          <span style={cardCheckboxStyle} />
          <span style={cardTxnNameStyle}>{t.name}</span>
          <span style={{ ...cardTxnAmtStyle, color: t.kind === 'in' ? mkGreen : brand.ink }}>{t.amt}</span>
        </div>
      ))}
      <div style={cardEodStyle}>
        <span style={cardEodLblStyle}>End of day</span>
        <span style={cardEodValStyle}>{eod}</span>
      </div>
    </div>
  );
}

const appCardStyle: React.CSSProperties = {
  background: '#fff', border: `1px solid ${mkLine}`, borderRadius: 18, overflow: 'hidden',
  boxShadow: '0 30px 60px -20px rgba(8,12,17,.18), 0 6px 18px rgba(8,12,17,.06)',
  width: '100%', maxWidth: 460, position: 'relative', zIndex: 1,
};

// header
const cardHeadStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: `1px solid ${mkLine}`,
};
const cardToggleStyle: React.CSSProperties = {
  display: 'flex', gap: 2, padding: 3, background: brand.paper, border: `1px solid ${mkLine}`, borderRadius: 999, marginLeft: 8,
};
const cardSegStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 22, borderRadius: 999, color: mkMute,
};
const cardSegActiveStyle: React.CSSProperties = {
  background: '#fff', color: brand.ink, boxShadow: '0 1px 3px rgba(0,0,0,.06)',
};
const cardBellStyle: React.CSSProperties = {
  width: 30, height: 30, borderRadius: 999, border: `1px solid ${mkLine}`,
  display: 'flex', alignItems: 'center', justifyContent: 'center', color: mkMute, flexShrink: 0,
};
const cardNewBtnStyle: React.CSSProperties = {
  background: brand.ink, color: '#fff', borderRadius: 8, padding: '7px 12px', fontSize: 12.5, fontWeight: 500, whiteSpace: 'nowrap',
};
const cardLogoutBtnStyle: React.CSSProperties = {
  border: `1px solid ${mkLine}`, color: mkMute, borderRadius: 8, padding: '6px 11px', fontSize: 12, whiteSpace: 'nowrap',
};

// balance + stats + search
const cardBalStyle: React.CSSProperties = { padding: '14px 16px 12px', borderBottom: `1px solid ${mkLine}` };
const cardBalRowStyle: React.CSSProperties = { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 };
const cardOverlineStyle: React.CSSProperties = { fontSize: 12, color: mkMute, textTransform: 'uppercase', letterSpacing: 0.8 };
const cardBalNumStyle: React.CSSProperties = {
  fontFamily: fonts.mono, fontSize: 26, fontWeight: 500, letterSpacing: -1, color: brand.ink,
  display: 'inline-flex', alignItems: 'center', gap: 7,
};
const cardStatsRowStyle: React.CSSProperties = { display: 'flex', gap: 12, marginTop: 12 };
const cardStatLblStyle: React.CSSProperties = { fontSize: 9.5, color: mkMute, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 };
const cardStatValStyle: React.CSSProperties = { fontFamily: fonts.mono, fontSize: 15, fontWeight: 500, letterSpacing: -0.3, color: mkGreen };
const cardStatSubStyle: React.CSSProperties = { fontSize: 9.5, color: mkMute, marginTop: 2 };
const cardSearchStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: `1px solid ${mkLine}`,
  borderRadius: 8, padding: '7px 11px', marginTop: 11, color: mkMute, fontSize: 13,
};

// day groups
const cardDayHeadStyle: React.CSSProperties = { display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 8 };
const cardDowStyle: React.CSSProperties = { fontFamily: fonts.mono, fontSize: 10.5, color: mkMute, letterSpacing: 0.6, textTransform: 'uppercase' };
const cardDayNumStyle: React.CSSProperties = { fontFamily: fonts.mono, fontSize: 15, fontWeight: 500, color: brand.ink, letterSpacing: -0.5 };
const cardDayMonStyle: React.CSSProperties = { fontSize: 11, color: mkMute };
const cardTxnStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: `1px solid ${mkLine}`,
  borderRadius: 10, padding: '10px 12px', marginBottom: 6,
};
const cardCheckboxStyle: React.CSSProperties = { width: 18, height: 18, border: '1.5px solid #cdd2cf', borderRadius: 5, flexShrink: 0 };
const cardTxnNameStyle: React.CSSProperties = { flex: 1, fontSize: 13.5, color: brand.ink, fontWeight: 500, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const cardTxnAmtStyle: React.CSSProperties = { fontFamily: fonts.mono, fontSize: 13, flexShrink: 0 };
const cardEodStyle: React.CSSProperties = { display: 'flex', alignItems: 'baseline', justifyContent: 'flex-end', gap: 8, marginTop: 8 };
const cardEodLblStyle: React.CSSProperties = { fontSize: 10, color: mkMute, textTransform: 'uppercase', letterSpacing: 0.6 };
const cardEodValStyle: React.CSSProperties = { fontFamily: fonts.mono, fontSize: 15, fontWeight: 500, color: brand.ink };

function Features() {
  return (
    <section id="why" className="mk-features" style={featuresStyle}>
      <FeatureCard
        icon={<PoolDishMark size={40} color={brand.lagoon} />}
        title={<>One balance.<br />Always honest.</>}
        body="Your projected balance accounts for every scheduled bill, paycheck, and one-off transfer. No “available” vs “actual” — just the truth, two weeks out."
      />
      <FeatureCard
        icon={
          <svg width="40" height="40" viewBox="0 0 40 40" aria-hidden>
            <g stroke={brand.lagoon} strokeWidth="2.4" fill="none" strokeLinecap="round">
              <path d="M4 28 L 12 20 L 18 24 L 26 12 L 36 18" />
              <circle cx="4" cy="28" r="2.4" fill={brand.lagoon} />
              <circle cx="36" cy="18" r="2.4" fill={brand.lagoon} />
            </g>
          </svg>
        }
        title={<>Watch the curve,<br />not the spreadsheet.</>}
        body="See exactly what your balance will be on any given day. No formulas, no envelopes, no monthly reset."
      />
      <FeatureCard
        icon={
          <svg width="40" height="40" viewBox="0 0 40 40" aria-hidden>
            <g stroke={brand.lagoon} strokeWidth="2.4" fill="none" strokeLinecap="round" strokeLinejoin="round">
              <rect x="6" y="9" width="28" height="24" rx="3" />
              <path d="M6 16 L 34 16 M 13 5 v 6 M 27 5 v 6" />
              <circle cx="14" cy="24" r="1.5" fill={brand.lagoon} />
              <circle cx="20" cy="24" r="1.5" fill={brand.lagoon} />
              <circle cx="26" cy="24" r="1.5" fill={brand.lagoon} />
            </g>
          </svg>
        }
        title={<>Set it.<br />Forget it. Trust it.</>}
        body="Plug in your paychecks once. Add the rent, the streaming bundle, the car payment. Gooey reschedules itself when payday lands on a weekend."
      />
    </section>
  );
}

const featuresStyle: React.CSSProperties = {
  maxWidth: 1280, margin: '96px auto 0', padding: '0 32px',
  display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24,
};

function FeatureCard({ icon, title, body }: { icon: React.ReactNode; title: React.ReactNode; body: string }) {
  return (
    <div className="mk-feature-card" style={featureCardStyle}>
      <div style={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</div>
      {/* h2: these sit directly under the hero h1, so jumping to h3 would skip a
          level (Lighthouse "heading elements not in sequentially-descending order"). */}
      <h2 style={featureH3Style}>{title}</h2>
      <p style={featurePStyle}>{body}</p>
    </div>
  );
}

const featureCardStyle: React.CSSProperties = {
  background: '#fff', border: '1px solid #e6e2d8', borderRadius: 18, padding: '32px 28px',
  display: 'flex', flexDirection: 'column', gap: 14, minHeight: 240,
};
const featureH3Style: React.CSSProperties = {
  fontFamily: fonts.display, fontWeight: 600, fontSize: 22, letterSpacing: -0.4,
  margin: 0, lineHeight: 1.15, color: brand.ink,
};
const featurePStyle: React.CSSProperties = { color: '#5d6671', fontSize: 14.5, lineHeight: 1.5, margin: 0 };

// ── Gooey+ premium section ──────────────────────────────────────────────────
// A premium-feeling deep-lagoon band marketing the live paid tier: the power
// features, then a public Free-vs-Gooey+ comparison with pricing so visitors
// can see what they'd pay before signing up. The CTA is the free Get-started
// (you sign up free and upgrade in-app). Feature list mirrors
// docs/gooey-plus-plan.md (§7.3); the comparison + prices mirror the in-app
// billing page (BillingPage.tsx COMPARE_ROWS + PlanPicker).

type GplusFeature = { title: string; body: string; icon: React.ReactNode };

const aqua = brand.lagoonAqua;
const gpIcon = (path: React.ReactNode): React.ReactNode => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={aqua} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {path}
  </svg>
);

const GPLUS_FEATURES: GplusFeature[] = [
  {
    title: 'Connect your bank',
    body: 'Securely link your accounts through Plaid — no more typing balances in by hand.',
    icon: gpIcon(<><path d="M3 10l9-6 9 6" /><path d="M4 10v9h16v-9" /><path d="M9 19v-5h6v5" /></>),
  },
  {
    title: 'Live balance updates',
    body: 'Your balance reflects what’s actually in the bank, refreshed automatically.',
    icon: gpIcon(<><path d="M21 12a9 9 0 11-3-6.7" /><path d="M21 4v4h-4" /></>),
  },
  {
    title: 'Auto-clear transactions',
    body: 'Real purchases match your scheduled items and check themselves off.',
    icon: gpIcon(<><path d="M3.5 12.5l5 5 12-12" /></>),
  },
  {
    title: 'Smart categories',
    body: 'Spending sorted automatically, with insight into where the money goes.',
    icon: gpIcon(<><path d="M20.6 13.4l-7.2 7.2a2 2 0 01-2.8 0l-6.2-6.2A2 2 0 014 13V5a1 1 0 011-1h8a2 2 0 011.4.6l6.2 6.2a2 2 0 010 2.6z" /><circle cx="8" cy="8" r="1.3" /></>),
  },
  {
    title: 'Up to 4 accounts',
    body: 'Track checking, savings, and more — all on one honest timeline.',
    icon: gpIcon(<><rect x="3" y="6" width="14" height="10" rx="2" /><path d="M7 18h12a2 2 0 002-2V9" /></>),
  },
  {
    title: 'Bigger households',
    body: 'Share one financial picture with the whole household, not just two.',
    icon: gpIcon(<><circle cx="9" cy="8" r="3" /><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" /><path d="M16 5.5a3 3 0 010 5.8" /><path d="M21 20c0-2.6-1.7-4.8-4-5.6" /></>),
  },
  {
    title: 'What-if scenarios',
    body: 'Test a new car payment or a raise before it ever touches your real ledger.',
    icon: gpIcon(<><path d="M6 3v6a6 6 0 006 6 6 6 0 006-6V3" /><path d="M6 21a3 3 0 100-6 3 3 0 000 6z" /><path d="M18 9a3 3 0 100-6 3 3 0 000 6z" /></>),
  },
  {
    title: 'Goals + auto-plan',
    body: 'Set a target and let Gooey map the contributions to reach it.',
    icon: gpIcon(<><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="4" /><circle cx="12" cy="12" r="0.6" fill={aqua} /></>),
  },
];

// Public Free-vs-Gooey+ comparison. Mirrors the in-app billing page's
// COMPARE_ROWS so the two never drift; `true` renders a check, `false` a dash,
// and a string renders as the per-plan value.
type PlanValue = string | boolean;
type PlanRow = { label: string; free: PlanValue; plus: PlanValue };

const PLAN_ROWS: PlanRow[] = [
  { label: 'Schedule, calendar & balance projections', free: true, plus: true },
  { label: 'Finance accounts', free: '1', plus: 'Up to 4' },
  { label: 'Household members', free: '2', plus: 'Up to 6' },
  { label: 'Connect your bank & auto-update balances', free: false, plus: true },
  { label: 'Auto-clear transactions', free: false, plus: true },
  { label: 'Smart categories & spending insights', free: false, plus: true },
  { label: 'What-if scenario simulation', free: false, plus: true },
  { label: 'Savings goals with a contribution plan', free: false, plus: true },
];

function GooeyPlus() {
  return (
    <section id="gooey-plus" className="mk-gplus" style={gplusStyle}>
      <div style={gplusHeadStyle}>
        <span style={gplusBadgeStyle}>Now available</span>
        <h2 className="mk-gplus-h2" style={gplusH2Style}>
          Gooey<span style={{ color: aqua, fontStyle: 'normal' }}>+</span>
        </h2>
        <p style={gplusLedeStyle}>
          Everything you love about Gooey, plus a live connection to your real bank — so the manual
          work disappears and the numbers are always true.
        </p>
      </div>
      <div className="mk-gplus-grid" style={gplusGridStyle}>
        {GPLUS_FEATURES.map((f) => (
          <div key={f.title} style={gplusItemStyle}>
            <div style={gplusIconWrapStyle}>{f.icon}</div>
            <div>
              <h3 style={gplusItemTitleStyle}>{f.title}</h3>
              <p style={gplusItemBodyStyle}>{f.body}</p>
            </div>
          </div>
        ))}
      </div>
      <PlanCompare />
      <div style={gplusCtaRowStyle}>
        {registrationEnabled ? (
          <>
            <Link to="/register" style={gplusCtaStyle}>Get started free</Link>
            <span style={gplusCtaNoteStyle}>
              Start free — Gooey+ is an optional upgrade with a 14-day free trial. The scheduler stays free.
            </span>
          </>
        ) : (
          <>
            <Link to="/login" style={gplusCtaStyle}>Log in</Link>
            <span style={gplusCtaNoteStyle}>
              New sign-ups are paused right now. Gooey+ is an optional upgrade with a
              14-day free trial; the scheduler stays free.
            </span>
          </>
        )}
      </div>
    </section>
  );
}

function PlanCompare() {
  return (
    <div className="mk-plan-compare" style={planCompareStyle}>
      <PlanColumn
        title="Free"
        price="$0"
        priceSub="forever"
        which="free"
      />
      <PlanColumn
        title={<>Gooey<span style={{ color: aqua }}>+</span></>}
        price="$8.99"
        priceSub="/ mo — or $79/yr (save 27%)"
        which="plus"
        highlight
      />
    </div>
  );
}

function PlanColumn({
  title, price, priceSub, which, highlight = false,
}: {
  title: React.ReactNode; price: string; priceSub: string; which: 'free' | 'plus'; highlight?: boolean;
}) {
  return (
    <section style={planColStyle(highlight)}>
      <div style={planColTitleStyle}>{title}</div>
      <div style={planPriceRowStyle}>
        <span style={planPriceStyle}>{price}</span>
        <span style={planPriceSubStyle}>{priceSub}</span>
      </div>
      <ul style={planListStyle}>
        {PLAN_ROWS.map((row) => {
          const value = row[which];
          const included = value !== false;
          return (
            <li key={row.label} style={planLiStyle(included)}>
              <span aria-hidden style={planTickStyle(included, highlight)}>
                {included ? '✓' : '–'}
              </span>
              <span>{row.label}</span>
              {typeof value === 'string' && <span style={planValStyle(highlight)}>{value}</span>}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

const gplusStyle: React.CSSProperties = {
  maxWidth: 1280, margin: '120px auto 0', padding: '80px 48px',
  background: brand.lagoonDeep, color: brand.paper, borderRadius: 28, position: 'relative', overflow: 'hidden',
  // The nav is position:sticky, so an anchor jump parks the section top flush
  // under the (overlapping) nav and clips the badge. Offset the scroll target
  // by the nav's height so the band lands just below it.
  scrollMarginTop: 88,
};
const gplusHeadStyle: React.CSSProperties = {
  maxWidth: 620, margin: '0 auto', textAlign: 'center',
};
const gplusBadgeStyle: React.CSSProperties = {
  display: 'inline-block', fontFamily: fonts.mono, fontSize: 11, letterSpacing: 1.4,
  textTransform: 'uppercase', color: brand.lagoonDeep, background: aqua,
  borderRadius: 999, padding: '5px 12px', fontWeight: 600,
};
const gplusH2Style: React.CSSProperties = {
  fontFamily: fonts.brand, fontWeight: 400, fontSize: 'clamp(48px, 7vw, 80px)',
  lineHeight: 1, letterSpacing: -2, margin: '18px 0 0', color: brand.paper,
};
const gplusLedeStyle: React.CSSProperties = {
  fontSize: 17, lineHeight: 1.55, color: brand.lagoonAqua, margin: '16px 0 0',
};
const gplusGridStyle: React.CSSProperties = {
  maxWidth: 980, margin: '48px auto 0', display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)', gap: 18,
};
const gplusItemStyle: React.CSSProperties = {
  display: 'flex', gap: 14, alignItems: 'flex-start',
  background: 'rgba(255,255,255,.05)', border: `1px solid ${brand.lagoonMid}55`,
  borderRadius: 16, padding: '18px 20px',
};
const gplusIconWrapStyle: React.CSSProperties = {
  flexShrink: 0, width: 40, height: 40, borderRadius: 10,
  background: 'rgba(191,225,227,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const gplusItemTitleStyle: React.CSSProperties = {
  fontFamily: fonts.display, fontWeight: 600, fontSize: 17, letterSpacing: -0.3,
  margin: 0, color: brand.paper,
};
const gplusItemBodyStyle: React.CSSProperties = {
  fontSize: 13.5, lineHeight: 1.5, color: brand.lagoonAqua, margin: '5px 0 0',
};
const gplusCtaRowStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, marginTop: 44, textAlign: 'center',
};
const gplusCtaStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 8,
  background: aqua, color: brand.lagoonDeep, textDecoration: 'none',
  fontSize: 15, fontWeight: 600, borderRadius: 999, padding: '14px 26px',
};
const gplusCtaNoteStyle: React.CSSProperties = {
  fontSize: 13, color: `${brand.lagoonAqua}cc`,
};

// Free-vs-Gooey+ pricing comparison (two columns on the dark band).
const planCompareStyle: React.CSSProperties = {
  maxWidth: 760, margin: '56px auto 0', display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)', gap: 18, alignItems: 'start',
};
const planColStyle = (highlight: boolean): React.CSSProperties => ({
  background: highlight ? 'rgba(191,225,227,.10)' : 'rgba(255,255,255,.05)',
  border: `${highlight ? 2 : 1}px solid ${highlight ? aqua : `${brand.lagoonMid}55`}`,
  borderRadius: 18, padding: '24px 24px 26px',
});
const planColTitleStyle: React.CSSProperties = {
  fontFamily: fonts.brand, fontWeight: 400, fontSize: 26, letterSpacing: -1, color: brand.paper,
};
const planPriceRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'baseline', gap: 8, margin: '6px 0 18px', flexWrap: 'wrap',
};
const planPriceStyle: React.CSSProperties = {
  fontFamily: fonts.mono, fontSize: 30, fontWeight: 500, letterSpacing: -1, color: brand.paper,
};
const planPriceSubStyle: React.CSSProperties = {
  fontSize: 12.5, color: brand.lagoonAqua,
};
const planListStyle: React.CSSProperties = {
  listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 11,
};
const planLiStyle = (included: boolean): React.CSSProperties => ({
  display: 'flex', gap: 9, alignItems: 'baseline', fontSize: 13.5, lineHeight: 1.4,
  // Excluded rows are dimmed but must still clear WCAG AA (4.5:1) on the deep
  // lagoon band — lagoonAqua at 0x99 alpha failed; 0xcc passes (~5.3:1).
  color: included ? brand.paper : `${brand.lagoonAqua}cc`,
});
const planTickStyle = (included: boolean, highlight: boolean): React.CSSProperties => ({
  flexShrink: 0, width: 14, textAlign: 'center', fontWeight: 700,
  color: included ? (highlight ? aqua : brand.paper) : `${brand.lagoonAqua}cc`,
});
const planValStyle = (highlight: boolean): React.CSSProperties => ({
  marginLeft: 'auto', paddingLeft: 8, fontWeight: 600, whiteSpace: 'nowrap',
  color: highlight ? aqua : brand.paper,
});

function Footer() {
  return (
    <footer className="mk-footer" style={footerStyle}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
        <div style={brandLinkStyle}>
          <PoolDishMark size={26} color={brand.lagoon} />
          <Wordmark c={c} size={22} />
        </div>
        <div style={footerLinksStyle}>
          <Link to="/blog" style={footerLinkStyle}>Blog</Link>
          <span aria-hidden style={{ color: '#cdd2cf' }}>·</span>
          <Link to="/privacy" style={footerLinkStyle}>Privacy Policy</Link>
          <span aria-hidden style={{ color: '#cdd2cf' }}>·</span>
          <Link to="/terms" style={footerLinkStyle}>Terms of Use</Link>
        </div>
        <div style={legalStyle}>© 2026 Gooey Finance, LLC</div>
      </div>
    </footer>
  );
}

const footerLinksStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12, fontSize: 13,
};
const footerLinkStyle: React.CSSProperties = {
  color: '#5d6671', textDecoration: 'none',
};

const footerStyle: React.CSSProperties = {
  maxWidth: 1280, margin: '120px auto 0', padding: '48px 32px',
  borderTop: '1px solid #e6e2d8',
  display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 32, flexWrap: 'wrap',
};
const legalStyle: React.CSSProperties = {
  fontFamily: fonts.mono, fontSize: 11, color: '#5d6671', letterSpacing: 0.8, textTransform: 'uppercase',
};
